import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';
import { UserClaimsService, UserInfoService } from '@fxp/fxpservices';

/**
 * CalendarAuthorizationService
 *
 * Faithful port of the calendar-relevant slice of the legacy GRM Experience
 * `authorization.service.js`. It reuses the same FXP `UserClaimsService` claims
 * (PS + FxPPlatform tenants) and the same resource-permission model so that the
 * React yearly calendar gates cross-user viewing exactly like the old AngularJS
 * `calendar.controller.js` did.
 *
 * The backend Calendar APIs remain the authoritative authority; this gate only
 * mirrors the legacy claims check for UX and least-privilege routing.
 */

interface ResourcePermission {
  resource: string;
  permissions: string[];
}

interface UserClaims {
  alias: string;
  roles: { [roleName: string]: { roleName?: string } | undefined };
  resourcePermissions: ResourcePermission[];
}

// Tenants the legacy authorization.service.js merged claims from.
const CLAIM_TENANTS = ['PS', 'FxPPlatform'];

// Roles allowed to view another user's calendar (legacy calendar.controller.js gate).
const RESOURCE_MANAGER_ROLE = 'ResourceManagement';
/**
 * Legacy `isFunctionalManager()` (authorization.service.js:221-229) returns true for EITHER
 * FunctionalManagement OR ServicesLeadership. Omitting the second denied Services Leadership
 * users a team calendar they can open in production - a fail-closed parity break, but a real
 * loss of access, and one that widens as this page takes over the Resource Dashboard routes.
 */
const FUNCTIONAL_MANAGER_ROLES = ['FunctionalManagement', 'ServicesLeadership'];

// $stateParams.role values accepted by the legacy cross-user gate.
const CROSS_USER_ROLE_PARAMS = ['resourcemanager', 'functionalmanager'];

/**
 * Permission inheritance map, copied verbatim from grm.config.json
 * `PermissionMappingDictionary`. A holder of a broader permission (e.g. Read_All)
 * implicitly satisfies narrower ones (e.g. Read_Team), matching legacy behaviour.
 */
const PERMISSION_MAPPING: { [permission: string]: string[] } = {
  All: [
    'Add',
    'Edit',
    'View',
    'Read_Team',
    'Read_All',
    'Write_Team',
    'Write_All',
    'Accrediation_Override',
    'Request',
    'Approve',
  ],
  Read_All: ['View', 'Read_Team'],
  Read_Team: ['View'],
  Write_All: ['Add', 'Edit', 'Write_Team'],
  Write_Team: ['Add', 'Edit'],
};

export class CalendarAuthorizationService {
  private static instance: CalendarAuthorizationService;
  private userClaims: UserClaims | null = null;

  private constructor() {}

  public static getInstance(): CalendarAuthorizationService {
    if (!CalendarAuthorizationService.instance) {
      CalendarAuthorizationService.instance = new CalendarAuthorizationService();
    }
    return CalendarAuthorizationService.instance;
  }

  /**
   * Replicates calendar.controller.js cross-user gate: a different user's calendar
   * may only be viewed when the logged-in user has team calendar access AND the
   * route role denotes a resource/functional manager.
   *
   * Defense-in-depth : the URL-supplied role is bound to a role the caller ACTUALLY
   * holds, rather than trusting the caller-supplied role string alone. This prevents a
   * caller from elevating to a manager view simply by typing a role in the URL.
   *
   * NOTE: This is a UX gate only. It CANNOT close the IDOR on its own — the Calendar
   * backend remains the authoritative authority and MUST enforce self-or-managed-team
   * membership for the requested alias server-side (pair each permission with its
   * required resource; verify the target belongs to the caller's managed team).
   */
  public canViewOtherUserCalendar(roleParam: string | undefined | null): boolean {
    const role = (roleParam || '').toLowerCase();
    if (!this.hasTeamCalendarAccess() || CROSS_USER_ROLE_PARAMS.indexOf(role) === -1) {
      return false;
    }
    if (role === 'resourcemanager') {
      return this.isResourceManager();
    }
    if (role === 'functionalmanager') {
      return this.isFunctionalManager();
    }
    return false;
  }

  /** Legacy hasTeamCalendarAccess(): Read_Team on both ProjectAssignment and NonProjectAssignment. */
  public hasTeamCalendarAccess(): boolean {
    return (
      this.hasResourcePermission({ resource: 'ProjectAssignment', permissions: ['Read_Team'] }) &&
      this.hasResourcePermission({ resource: 'NonProjectAssignment', permissions: ['Read_Team'] })
    );
  }

  /**
   * Whether the viewer may EDIT another user's calendar.
   *
   * Cross-user is editable in production and always has been: `IsExperienceEditable` is only
   * forced false for the self view (calendar.controller.js:80,3020-3026), and the
   * `grm-resourcedashboardfm` state carries **Write_Team** on NonProjectAssignment
   * (grm.config.json:780-793). Gating edit on `isViewingOtherUser` instead was STRICTER than
   * prod, and was the reason the Monthly toggle had to bounce managers out to the legacy page.
   *
   * Same caveat as canViewOtherUserCalendar: a UX gate only. The server remains authoritative.
   */
  public canEditOtherUserCalendar(): boolean {
    return this.hasResourcePermission({
      resource: 'NonProjectAssignment',
      permissions: ['Write_Team'],
    });
  }

  public isResourceManager(): boolean {
    return this.hasNamedRole(RESOURCE_MANAGER_ROLE);
  }

  public isFunctionalManager(): boolean {
    return FUNCTIONAL_MANAGER_ROLES.some((roleName) => this.hasNamedRole(roleName));
  }

  private hasNamedRole(roleName: string): boolean {
    const claims = this.getClaims();
    const role = claims && claims.roles ? claims.roles[roleName] : undefined;
    return !!(role && role.roleName != null);
  }

  /**
   * Legacy hasResourcePermission(): true when the user holds the permission directly,
   * holds 'All' on ResourceManagement, or holds a broader permission that maps to it.
   */
  private hasResourcePermission(required: ResourcePermission): boolean {
    const claims = this.getClaims();
    if (!claims) {
      return false;
    }

    const hasAllResourceManagement = claims.resourcePermissions.some(
      (p) => p.resource === 'ResourceManagement' && p.permissions.indexOf('All') > -1
    );
    if (hasAllResourceManagement) {
      return true;
    }

    const userPermission = claims.resourcePermissions.find((p) => p.resource === required.resource);
    if (!userPermission) {
      return false;
    }

    return required.permissions.some((permission) => {
      if (userPermission.permissions.indexOf(permission) > -1) {
        return true;
      }
      return userPermission.permissions.some(
        (held) => (PERMISSION_MAPPING[held] || []).indexOf(permission) > -1
      );
    });
  }

  /** Loads and caches claims for the current user, mirroring legacy performAuthorization(). */
  private getClaims(): UserClaims | null {
    try {
      const loggedInUserAlias = this.getUserInfoService().getCurrentUser();
      if (this.userClaims && this.userClaims.alias === loggedInUserAlias) {
        return this.userClaims;
      }

      const claimsService = this.getUserClaimsService();
      const roles: UserClaims['roles'] = {};
      let resourcePermissions: ResourcePermission[] = [];

      CLAIM_TENANTS.forEach((tenantId) => {
        const tenantClaims = claimsService.getUserTenantClaims(tenantId);
        const claims = tenantClaims && tenantClaims.claims ? tenantClaims.claims : tenantClaims;
        if (!claims) {
          return;
        }
        if (claims.roles) {
          Object.keys(claims.roles).forEach((roleName) => {
            roles[roleName] = claims.roles[roleName];
          });
        }
        if (Array.isArray(claims.resourcePermissions)) {
          resourcePermissions = resourcePermissions.concat(claims.resourcePermissions);
        }
      });

      this.userClaims = { alias: loggedInUserAlias, roles, resourcePermissions };
      return this.userClaims;
    } catch {
      // Fail closed, matching legacy behaviour (no claims -> not authorized).
      return null;
    }
  }

  private getUserClaimsService(): UserClaimsService {
    return FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.UserClaimsService
    ) as UserClaimsService;
  }

  private getUserInfoService(): UserInfoService {
    return FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.UserInfoService
    ) as UserInfoService;
  }
}

export default CalendarAuthorizationService;
